import { Box, Stack, SxProps, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { CompletitionStatus, InfoContainer, LinearProgressCustom } from "./CarouselCard.style";
import { ComplexityLevel } from "../../components/Commons/ComplexityLevel";
import { LEVEL, STATUS_LO } from "../../consts";
import { OutletContext } from "../../routes";
import { useNavigate } from "../../services";
import type { Level, StatusLO } from "../../types";
import { TruncatedText } from "../TruncatedText";

type IInfo = {
  title: string;
  progression: string;
  status: StatusLO;
  learningObjectTypology: Level;
  onClick?: () => void;
  sx?: SxProps;
  disabled?: boolean;
  courseId?: number;
  id: number;
  rootId?: number;
  grandParentId?: number;
  externalFileUrl?:string;
  isLinkRelative?:boolean;
  isEditorial?:boolean;
  complexity?: "BASE" | "MEDIUM" | "ADVANCED";
}

export const Info = ({
  title,
  progression,
  onClick,
  sx,
  status,
  learningObjectTypology,
  courseId,
  id,
  rootId,
  disabled,
  grandParentId,
  externalFileUrl,
  isLinkRelative,
  isEditorial,
  complexity
}: IInfo) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isForYou = window.location.href.includes("per-te");
  const clampRef = useRef<HTMLElement | null>(null);
 
  const { sectionUrl } = useOutletContext<OutletContext>();

  const showProgressBar = (!!(progression && (status === STATUS_LO.P || status === STATUS_LO.C) && (learningObjectTypology === LEVEL.PATH || learningObjectTypology === LEVEL.COURSE)));

  function handleProgressionValue() {
    if (progression && progression !== "0%") {
      return Number(progression.replace("%", ""));
    }
    return 1;
  }
  function handleProgressionText() {
    if(!progression) return "0%";

    const regex = /^.*%$/;
    
    if (progression && progression.toString().match(regex)) {
      return progression;
    }
    return progression + "%";
  }

  function handleRedirectUrl() {
    if (isLinkRelative && externalFileUrl) {
      let cleanedLink = externalFileUrl;

      if (!cleanedLink.startsWith("/")) cleanedLink = "/" + cleanedLink;
      const regex = /\/$/; //check if url has / at the end
      if(regex.test(externalFileUrl)){
        return `${cleanedLink}${id}`; //link url and id without / in between
      }
      return `${cleanedLink}/${id}`; //link url and id with / in between
    }
    return `${externalFileUrl}`;
  }

  return (
    <InfoContainer onClick={ onClick && onClick } sx={ sx }>
      { sectionUrl === "per-te" && complexity && ( 
        <ComplexityLevel complexity={ complexity }/>
      ) } 
      <div style={ { display: "flex", flexDirection: "column", minHeight: "fit-content" } }>
        <Typography fontSize="1rem" fontWeight="600"
          sx={ {
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: "1.25rem",
            maxHeight: "2.5rem"
          } }>
          <TruncatedText
            clampRef={ clampRef }
            text={ title }
            disabled={ disabled }
            actionOnEditorial={ handleRedirectUrl }
            isEditorial={ isEditorial }
            href={ navigate.getDetailLink({
              courseId, id, isForYou, learningObjectTypology, rootId: rootId ?? grandParentId
            }) }
            isLinkRelative={ isLinkRelative }
          />
        </Typography>
     
        { showProgressBar && (
          <Stack direction={ "column" } className={ "carousel-card-progress-bar" }>
            <CompletitionStatus id={ "completion-status" }>
              <Box
                sx={ {
                  height: "1.44rem",
                  display: "flex",
                  alignItems: "center"
                } }
              >
                <Typography
                  color={ theme.customColors.textPrimary }
                  fontSize={ "0.75rem" }
                >
                  { t("progress") }
                </Typography>
              </Box>

              <Typography
                color={ theme.customColors.textPrimary }
                fontSize={ "0.75rem" }
                display="flex"
                alignItems="center"
              >
                { handleProgressionText() }
              </Typography>
            </CompletitionStatus>
            <LinearProgressCustom
              aria-label={ t("activity_progress") }
              variant="determinate"
              value={ handleProgressionValue() }
              $isCompleted={ progression === "100%" }
            />
          </Stack>
        )
        }
      </div>
    </InfoContainer>
  );
};
